import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Phone, MapPin, Send, CheckCircle, Loader2, Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const inquiryTypes = [
  { value: "speaking-corporate", label: "Speaking - Corporate Event" },
  { value: "speaking-conference", label: "Speaking - Conference" },
  { value: "speaking-church", label: "Speaking - Church/Ministry" },
  { value: "speaking-retreat", label: "Speaking - Retreat" },
  { value: "business-consulting", label: "Business Consulting" },
  { value: "wedding-officiating", label: "Wedding Officiating" },
  { value: "prayer-counseling", label: "Prayer & Counseling" },
  { value: "notary", label: "Notary Services" },
  { value: "rafha-entertainment", label: "Rafha Entertainment" },
  { value: "choice-mobile", label: "Choice Mobile Celebrations" },
  { value: "general", label: "General Inquiry" },
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    inquiryType: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, inquiryType: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          service: formData.inquiryType,
          message: formData.message,
        }),
      });
      
      if (response.ok) {
        setIsSubmitted(true);
        toast.success("Message sent! Check your email for confirmation.");
        setTimeout(() => {
          setFormData({ name: "", email: "", phone: "", inquiryType: "", message: "" });
          setIsSubmitted(false);
        }, 5000);
      } else {
        toast.error("Failed to send. Please try again or call directly.");
      }
    } catch (error) {
      toast.error("Network error. Please call (877) 295-5905 directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-28 bg-muted/30">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="bg-primary/10 text-primary border-primary/20 mb-4">
            <Mail className="w-3 h-3 mr-1" />
            Get In Touch
          </Badge>
          
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Let's Connect
          </h2>
          
          <p className="text-lg text-muted-foreground">
            Ready to transform your business, book a speaking engagement, or explore how we can work together?
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-10 max-w-6xl mx-auto" id="booking">
          {/* Contact Info */}
          <div className="space-y-6">
            <Card className="border-border/50">
              <CardContent className="p-6 space-y-4">
                <a 
                  href="tel:+18772955905" 
                  className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg hover:bg-primary/10 transition-colors"
                >
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">(877) 295-5905</p>
                    <p className="text-xs text-muted-foreground">Ext. 4 for Speaking</p>
                  </div>
                </a>
                
                <a 
                  href="mailto:info@lavistasolutions.com" 
                  className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg hover:bg-primary/10 transition-colors"
                >
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">info@lavistasolutions.com</p>
                    <p className="text-xs text-muted-foreground">Email anytime</p>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Montgomery, AL</p>
                    <p className="text-xs text-muted-foreground">Available nationwide</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-accent text-accent-foreground border-0">
              <CardContent className="p-6">
                <h4 className="font-bold text-cream mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gold" />
                  Response Time
                </h4>
                <p className="text-champagne/90 text-sm">
                  I personally respond to all inquiries within 24-48 business hours. 
                  For urgent matters, please call directly.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-border shadow-lg">
              <CardContent className="p-6 md:p-8">
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="font-display text-2xl font-bold text-foreground mb-2">
                      Message Sent!
                    </h3>
                    <p className="text-muted-foreground">
                      Thank you for reaching out. I'll be in touch soon!
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          placeholder="Your name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="bg-background"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="your@email.com"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="bg-background"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="(555) 000-0000"
                          value={formData.phone}
                          onChange={handleChange}
                          className="bg-background"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="inquiryType">I'm Interested In *</Label>
                        <Select value={formData.inquiryType} onValueChange={handleSelectChange} required>
                          <SelectTrigger className="bg-background">
                            <SelectValue placeholder="Select inquiry type" />
                          </SelectTrigger>
                          <SelectContent>
                            {inquiryTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="Tell me about your needs, event details, or how I can help you..."
                        value={formData.message}
                        onChange={handleChange}
                        required
                        className="bg-background min-h-[120px] resize-none"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-6 rounded-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Send className="w-4 h-4" />
                          Send Message
                        </span>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
